import type { FormEvent, KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
  Add20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import {
  ApiError,
  createClient,
  getWorkTypes,
  updateClient,
  uploadClientLogo,
  type ClientPayload,
  type Client,
  type WorkType,
} from "../../clients/clientsApi";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar clientes.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o cliente.";
};

const parseWorkTypesError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar tipos de obra.";
    }
  }

  return "Não foi possível carregar os tipos de obra.";
};

const normalizeWebsite = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export function NovoClienteView() {
  const navigate = useNavigate();
  const [condicoesPagamento, setCondicoesPagamento] =
    useState("30 dias");
  const [tiposObrasDisponiveis, setTiposObrasDisponiveis] =
    useState<WorkType[]>([]);
  const [tiposObrasSelecionados, setTiposObrasSelecionados] =
    useState<number[]>([]);
  const [tiposObrasErro, setTiposObrasErro] = useState<
    string | null
  >(null);
  const [tags, setTags] = useState<string[]>([]);
  const [novaTag, setNovaTag] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [createdClientId, setCreatedClientId] = useState<
    number | null
  >(null);
  const [nomeCliente, setNomeCliente] = useState("");
  const [nif, setNif] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [website, setWebsite] = useState("");
  const [morada, setMorada] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [localidade, setLocalidade] = useState("");
  const [pais, setPais] = useState("");
  const [contactoNome, setContactoNome] = useState("");
  const [funcao, setFuncao] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");
  const [contactoTelefone, setContactoTelefone] = useState("");
  const [notasInternas, setNotasInternas] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    const loadWorkTypes = async () => {
      setTiposObrasErro(null);
      try {
        const response = await getWorkTypes();
        if (!isMounted) return;
        setTiposObrasDisponiveis(response);
      } catch (error) {
        if (!isMounted) return;
        setTiposObrasErro(parseWorkTypesError(error));
      }
    };

    loadWorkTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleTipoObra = (tipoId: number) => {
    if (tiposObrasSelecionados.includes(tipoId)) {
      setTiposObrasSelecionados(
        tiposObrasSelecionados.filter((t) => t !== tipoId),
      );
    } else {
      setTiposObrasSelecionados([
        ...tiposObrasSelecionados,
        tipoId,
      ]);
    }
  };

  const adicionarTag = () => {
    if (novaTag.trim() && !tags.includes(novaTag.trim())) {
      setTags([...tags, novaTag.trim()]);
      setNovaTag("");
    }
  };

  const removerTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleKeyDownTag = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      adicionarTag();
    }
  };

  const handleAvatarChange = (file: File | null) => {
    setLogoFile(file);
  };

  const handleWebsiteBlur = () => {
    const normalized = normalizeWebsite(website);
    if (normalized && normalized !== website) {
      setWebsite(normalized);
      return;
    }

    if (!normalized && website.trim() !== website) {
      setWebsite(website.trim());
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!nomeCliente.trim()) {
      setSubmitError("O nome do cliente é obrigatório.");
      return;
    }

    setIsSaving(true);

    const normalizedWebsite = normalizeWebsite(website);

    const payload: ClientPayload = {
      name: nomeCliente.trim(),
      taxId: nif.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      website: normalizedWebsite,
      address: morada.trim() || null,
      postalCode: codigoPostal.trim() || null,
      city: localidade.trim() || null,
      country: pais.trim() || null,
      primaryContactName: contactoNome.trim() || null,
      primaryContactRole: funcao.trim() || null,
      primaryContactEmail: contactoEmail.trim() || null,
      primaryContactPhone: contactoTelefone.trim() || null,
      paymentTerms: condicoesPagamento,
      tags,
      workTypeIds: tiposObrasSelecionados,
      notes: notasInternas.trim() || null,
    };

    let savedClient: Client;

    try {
      if (createdClientId) {
        savedClient = await updateClient(createdClientId, payload);
      } else {
        savedClient = await createClient(payload);
        setCreatedClientId(savedClient.id);
      }
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (logoFile) {
      try {
        await uploadClientLogo(savedClient.id, logoFile);
      } catch {
        setSubmitError(
          "Cliente criado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/clientes", {
      state: { toast: "Cliente criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/clientes")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-cliente-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Cliente"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Cliente"
        subtitle="Cria um novo cliente e define o contacto principal."
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-cliente-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Cliente
                  </h3>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Logotipo do Cliente
                    </label>
                    <AvatarUploader
                      currentImage={null}
                      onImageChange={handleAvatarChange}
                      variant="entity"
                      label=""
                      subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="nomeCliente"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome do Cliente{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeCliente"
                      type="text"
                      placeholder="Insira o nome do cliente"
                      value={nomeCliente}
                      onChange={(event) =>
                        setNomeCliente(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="nif"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      NIF
                    </label>
                    <input
                      id="nif"
                      type="text"
                      placeholder="123456789"
                      value={nif}
                      onChange={(event) => setNif(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="cliente@email.pt"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="telefone"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Telefone
                    </label>
                    <input
                      id="telefone"
                      type="tel"
                      placeholder="+351 210 000 000"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="website"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Website
                    </label>
                    <input
                      id="website"
                      type="url"
                      placeholder="www.cliente.pt"
                      value={website}
                      onBlur={handleWebsiteBlur}
                      onChange={(event) =>
                        setWebsite(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="morada"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Morada
                    </label>
                    <input
                      id="morada"
                      type="text"
                      placeholder="Rua, número, andar"
                      value={morada}
                      onChange={(event) =>
                        setMorada(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="codigoPostal"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Código Postal
                    </label>
                    <input
                      id="codigoPostal"
                      type="text"
                      placeholder="1000-000"
                      value={codigoPostal}
                      onChange={(event) =>
                        setCodigoPostal(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="localidade"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Localidade
                    </label>
                    <input
                      id="localidade"
                      type="text"
                      placeholder="Lisboa"
                      value={localidade}
                      onChange={(event) =>
                        setLocalidade(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="pais"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      País
                    </label>
                    <input
                      id="pais"
                      type="text"
                      placeholder="Portugal"
                      value={pais}
                      onChange={(event) =>
                        setPais(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Contacto Principal
                  </h3>

                  <div>
                    <label
                      htmlFor="contactoNome"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome
                    </label>
                    <input
                      id="contactoNome"
                      type="text"
                      placeholder="Nome do contacto principal"
                      value={contactoNome}
                      onChange={(event) =>
                        setContactoNome(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="funcao"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Função / Cargo
                    </label>
                    <input
                      id="funcao"
                      type="text"
                      placeholder="Ex: Diretor Comercial"
                      value={funcao}
                      onChange={(event) =>
                        setFuncao(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contactoEmail"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="contactoEmail"
                      type="email"
                      placeholder="contacto@email.pt"
                      value={contactoEmail}
                      onChange={(event) =>
                        setContactoEmail(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contactoTelefone"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Telefone
                    </label>
                    <input
                      id="contactoTelefone"
                      type="tel"
                      placeholder="+351 910 000 000"
                      value={contactoTelefone}
                      onChange={(event) =>
                        setContactoTelefone(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Preferências & Tags
                  </h3>

                  <div>
                    <label
                      htmlFor="condicoesPagamento"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Condições de pagamento
                    </label>
                    <div className="relative">
                      <select
                        id="condicoesPagamento"
                        value={condicoesPagamento}
                        onChange={(e) =>
                          setCondicoesPagamento(e.target.value)
                        }
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                      >
                        <option value="Pronto pagamento">
                          Pronto pagamento
                        </option>
                        <option value="15 dias">15 dias</option>
                        <option value="30 dias">30 dias</option>
                        <option value="60 dias">60 dias</option>
                        <option value="90 dias">90 dias</option>
                      </select>
                      <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tipo de obras
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {tiposObrasDisponiveis.map((tipo) => (
                        <button
                          key={tipo.id}
                          type="button"
                          onClick={() => toggleTipoObra(tipo.id)}
                          className={`px-3 py-1.5 rounded transition-colors ${
                            tiposObrasSelecionados.includes(
                              tipo.id,
                            )
                              ? "bg-primary text-primary-foreground"
                              : "bg-secondary hover:bg-accent border border-border text-foreground"
                          }`}
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                            fontWeight:
                              tiposObrasSelecionados.includes(
                                tipo.id,
                              )
                                ? "var(--font-weight-semi-bold)"
                                : "var(--font-weight-normal)",
                          }}
                        >
                          {tipo.name}
                        </button>
                      ))}
                    </div>
                    {tiposObrasErro && (
                      <p className="mt-2 text-sm text-destructive">
                        {tiposObrasErro}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tags
                    </label>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {tags.map((tag) => (
                        <div
                          key={tag}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent border border-border rounded"
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => removerTag(tag)}
                            className="hover:text-primary transition-colors"
                          >
                            <Dismiss20Regular className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={novaTag}
                        onChange={(e) =>
                          setNovaTag(e.target.value)
                        }
                        onKeyDown={handleKeyDownTag}
                        placeholder="Adicionar nova tag"
                        className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={adicionarTag}
                        className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                        style={{
                          borderRadius: "var(--radius-button)",
                        }}
                      >
                        <Add20Regular className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Notas Internas
                  </h3>

                  <textarea
                    id="notasInternas"
                    rows={6}
                    placeholder="Adicione notas internas sobre este cliente. Esta informação não será partilhada com o cliente."
                    value={notasInternas}
                    onChange={(event) =>
                      setNotasInternas(event.target.value)
                    }
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
